import Privy, {
  getUserEmbeddedEthereumWallet,
  getEntropyDetailsFromUser,
  polygon,
  getSupportedChainById,
} from '@privy-io/js-sdk-core';
import { loadEthers } from '@/utils/load-ethers';
import {
  getWalletAddress,
  getWalletProviderId,
  getOriginLoginType,
  hasAuthSession,
} from '@/utils/auth-token';
import { getExpectedChainId } from '@/utils/wallet-chain';
import {
  isPrivyConfigured as isPrivyEnvConfigured,
  privyConfig,
  resolvePrivyAppOrigin,
} from '@/config/privy';
import { buildWalletLoginMessage, createWalletLoginNonce } from '@/utils/wallet-login-message';
import { createPrivyBrowserStorage, isStorageBlockedError } from '@/utils/client-storage';

let privyClient = null;
let iframe = null;
let messageListener = null;
let initPromise = null;

const IFRAME_PING_ATTEMPTS = 3;
const IFRAME_PING_DELAY_MS = 500;

function parsePrivyChainId(value) {
  if (typeof value === 'string' && value.startsWith('0x')) {
    return parseInt(value, 16);
  }
  return Number(value);
}

/** Privy 嵌入式钱包 supportedChains：与 VUE_APP_CHAIN_ID 对齐，默认 Polygon 优先 */
function resolvePrivySupportedChains() {
  const chainId = getExpectedChainId();
  const chain = getSupportedChainById(chainId) ?? polygon;
  return [chain];
}

export function isPrivyConfigured() {
  return isPrivyEnvConfigured();
}

export function getPrivyAppOrigin() {
  return resolvePrivyAppOrigin();
}

function teardownSecureIframe() {
  if (messageListener) {
    window.removeEventListener('message', messageListener);
    messageListener = null;
  }
  if (iframe) {
    iframe.remove();
    iframe = null;
  }
}

function setupSecureIframe(client) {
  if (!client || iframe) return;

  iframe = document.createElement('iframe');
  iframe.src = client.embeddedWallet.getURL();
  iframe.style.display = 'none';
  iframe.setAttribute('aria-hidden', 'true');
  document.body.appendChild(iframe);

  client.setMessagePoster(iframe.contentWindow);
  messageListener = (event) => {
    if (event.source !== iframe.contentWindow) return;
    try {
      const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      client.embeddedWallet.onMessage(data);
    } catch {
      // ignore non-Privy messages
    }
  };
  window.addEventListener('message', messageListener);
}

/** 登录/创建钱包/签名前：确认 Privy 隐藏 iframe 已就绪（不检查 wallet 是否已存在） */
async function ensurePrivyEmbeddedIframePing(client) {
  const c = client ?? (await getPrivyClient());
  if (!c) {
    throw new Error('Privy embedded wallet is not ready');
  }

  for (let attempt = 0; attempt < IFRAME_PING_ATTEMPTS; attempt += 1) {
    try {
      const pingOk = await c.embeddedWallet.ping(undefined);
      if (pingOk) return;
    } catch (error) {
      console.warn('[privy] iframe ping failed', error);
    }
    if (attempt < IFRAME_PING_ATTEMPTS - 1) {
      await new Promise((resolve) => {
        setTimeout(resolve, IFRAME_PING_DELAY_MS);
      });
    }
  }

  throw new Error('Privy embedded wallet is not ready');
}

/** 供登录 UI 映射 Privy 错误文案（返回 authDialog i18n key 后缀，无匹配则 null） */
export function resolvePrivyAuthErrorKey(err) {
  if (isStorageBlockedError(err)) return 'storageBlocked';

  const code = String(err?.code ?? err?.privyErrorCode ?? '').trim();
  if (code === 'linked_to_another_user') return 'privyLinkedToAnotherUser';
  if (code === 'invalid_credentials') return 'privyInvalidCredentials';
  if (code === 'disallowed_login_method') return 'privyGoogleNotAllowed';
  if (code === 'PRIVY_CUSTOM_TOKEN_NOT_FOUND') return 'privyCustomTokenNotFound';
  if (code === 'PRIVY_CUSTOM_TOKEN_SIGNER_MISSING') return 'privyCustomTokenSignerMissing';
  if (code === 'PRIVY_CUSTOM_TOKEN_EMPTY') return 'privyCustomTokenEmpty';

  const raw = String(err?.message ?? err?.error ?? '').toLowerCase();
  if (raw.includes('unable to access storage') || raw.includes('failed to access storage')) {
    return 'storageBlocked';
  }
  if (raw.includes('login with google not allowed') || raw.includes('google not allowed')) {
    return 'privyGoogleNotAllowed';
  }
  if (raw.includes('user-signer:sign') || raw.includes('reached timeout')) {
    return 'privySignTimeout';
  }
  if (raw.includes('embedded wallet is not ready')) {
    return 'privyEmbeddedNotReady';
  }
  if (raw.includes('签名未配置')) {
    return 'privyCustomTokenSignerMissing';
  }
  if (
    raw.includes('custom-token') &&
    (raw.includes('404') || raw.includes('not found') || raw.includes('status code 404'))
  ) {
    return 'privyCustomTokenNotFound';
  }
  if (
    err?.name === 'AbortError' ||
    raw.includes('signal is aborted') ||
    raw.includes('aborted without reason')
  ) {
    return 'networkUnstable';
  }
  if (
    raw.includes('auth prepare timeout') ||
    raw.includes('auth bootstrap timeout') ||
    raw.includes('privy auth prepare timeout')
  ) {
    return 'authPrepareTimeout';
  }
  // axios 裸 404（custom-token 未上线到当前后端）
  if (/status code 404/i.test(raw)) {
    return 'privyCustomTokenNotFound';
  }
  return null;
}

export async function initPrivy() {
  if (!isPrivyConfigured()) return null;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      privyClient = new Privy({
        appId: privyConfig.appId,
        ...(privyConfig.clientId ? { clientId: privyConfig.clientId } : {}),
        storage: createPrivyBrowserStorage(),
        supportedChains: resolvePrivySupportedChains(),
      });
      await privyClient.initialize();
      setupSecureIframe(privyClient);
      return privyClient;
    } catch (err) {
      privyClient = null;
      initPromise = null;
      teardownSecureIframe();
      throw err;
    }
  })();

  return initPromise;
}

export async function getPrivyClient() {
  if (!isPrivyConfigured()) return null;
  return initPrivy();
}

const PRIVY_AUTH_READY_TIMEOUT_MS = 8_000;

function withPrivyTimeout(promise, ms, message) {
  let timer;
  const timeoutPromise = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}

/** 邮箱 / OAuth 发码前：SDK + iframe 就绪（不要求 embedded wallet） */
export async function ensurePrivyAuthReady({ timeoutMs = PRIVY_AUTH_READY_TIMEOUT_MS } = {}) {
  const client = await withPrivyTimeout(getPrivyClient(), timeoutMs, 'privy auth prepare timeout');
  if (!client) {
    throw new Error('Privy is not configured');
  }
  if (!iframe) {
    setupSecureIframe(client);
  }
  return client;
}

/** 仅当存在 Privy 会话时才 logout，避免无意义 teardown 导致 AbortError */
export async function logoutPrivyIfSession() {
  const client = await getPrivyClient();
  if (!client) return;
  try {
    const { user } = await client.user.get();
    if (!user?.id) return;
  } catch {
    return;
  }
  await logoutPrivy();
}

async function ensureEmbeddedWallet(user, { createIfMissing = true } = {}) {
  const client = await getPrivyClient();
  if (!client || !user) {
    throw new Error('Privy is not ready');
  }

  let currentUser = user;
  let wallet = getUserEmbeddedEthereumWallet(currentUser);
  if (!wallet) {
    if (!createIfMissing) {
      const err = new Error('Privy embedded wallet missing');
      err.code = 'NO_EMBEDDED_WALLET';
      throw err;
    }
    await ensurePrivyEmbeddedIframePing(client);
    const created = await client.embeddedWallet.create({});
    currentUser = created.user ?? currentUser;
    wallet = getUserEmbeddedEthereumWallet(currentUser);
  }

  return { user: currentUser, wallet };
}

async function finalizePrivyLogin(session, { createIfMissing = true } = {}) {
  const embedded = await ensureEmbeddedWallet(session.user, { createIfMissing });
  const privyAccessToken =
    session.privy_access_token ??
    session.access_token ??
    session.token ??
    (await getPrivyAccessToken());

  return {
    ...embedded,
    token: session.token ?? privyAccessToken ?? null,
    refreshToken: session.refresh_token ?? session.refreshToken ?? null,
    privyAccessToken,
  };
}

const PRIVY_AUTH_API = 'https://auth.privy.io/api/v1';

/** 使用 access_token 调用 Privy GET /api/v1/users/me */
export async function fetchPrivyUserMe(accessToken) {
  const appId = privyConfig.appId;
  const token = String(accessToken ?? '').trim();
  if (!appId || !token) return null;

  const res = await fetch(`${PRIVY_AUTH_API}/users/me`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'privy-app-id': appId,
      'Content-Type': 'application/json',
    },
  });

  if (!res.ok) {
    throw new Error(`Privy users/me failed: ${res.status}`);
  }

  const data = await res.json();
  return data?.user ?? data;
}

/** 使用 Privy 嵌入式钱包对 message 进行 personal_sign */
export async function signMessageWithPrivyWallet(user, message) {
  const wallet = getUserEmbeddedEthereumWallet(user);
  const walletAddress = String(wallet?.address ?? '').trim();
  if (!walletAddress) {
    throw new Error('Privy embedded wallet address is missing');
  }

  await ensurePrivyEmbeddedIframePing();

  const provider = await getPrivyEmbeddedProvider();
  if (!provider?.request) {
    throw new Error('Privy embedded wallet provider is unavailable');
  }

  const signature = await provider.request({
    method: 'personal_sign',
    params: [message, walletAddress],
  });

  return { walletAddress, signature: String(signature ?? '').trim(), message };
}

/**
 * Privy 登录后：获取 access_token → users/me → 用嵌入式钱包签名 → 供后端 loginWithWallet 验签
 */
export async function createPrivyWalletLoginCredentials(user) {
  const accessToken = await getPrivyAccessToken();
  if (!accessToken) {
    throw new Error('Privy access token is missing');
  }

  try {
    await fetchPrivyUserMe(accessToken);
  } catch (error) {
    console.warn('[privy] users/me failed, continue with SDK user', error);
  }

  const nonce = createWalletLoginNonce();
  const wallet = getUserEmbeddedEthereumWallet(user);
  const walletAddress = String(wallet?.address ?? '').trim();
  if (!walletAddress) {
    throw new Error('Privy embedded wallet address is missing');
  }

  const message = buildWalletLoginMessage(nonce, walletAddress);
  const signed = await signMessageWithPrivyWallet(user, message);

  return {
    ...signed,
    accessToken,
  };
}

export async function privySendEmailCode(email) {
  const client = await getPrivyClient();
  if (!client) throw new Error('Privy is not configured');
  await client.auth.email.sendCode(email);
}

/**
 * @deprecated 多端钱包一致性：PC 邮箱改走平台 /login/email → custom-token → syncWithToken。
 * 勿用于登录主路径。
 */
export async function privyLoginWithEmailCode(email, code) {
  const client = await getPrivyClient();
  if (!client) throw new Error('Privy is not configured');
  const session = await client.auth.email.loginWithCode(email, code);
  return finalizePrivyLogin(session);
}

/**
 * 老账号迁移桥：仅当浏览器当前 Privy 会话正好持有后台登记钱包时，
 * 把平台 Custom JWT 身份链接到该旧 Privy 用户。此函数绝不 logout、绝不
 * 创建新钱包，失败也不会覆盖资金地址。
 */
export async function linkPlatformCustomAuthToRegisteredWallet(expectedAddress) {
  const expected = String(expectedAddress ?? '').trim();
  if (!expected) return { linked: false, code: 'NO_EXPECTED_WALLET' };

  const client = await getPrivyClient();
  if (!client) return { linked: false, code: 'NOT_CONFIGURED' };

  const currentUser = await getPrivySessionUser().catch(() => null);
  const currentWallet = getUserEmbeddedEthereumWallet(currentUser);
  const currentAddress = String(currentWallet?.address ?? '').trim();
  if (!currentUser?.id || !currentAddress) {
    return { linked: false, code: 'NO_LEGACY_SESSION' };
  }
  if (currentAddress.toLowerCase() !== expected.toLowerCase()) {
    return { linked: false, code: 'LEGACY_SESSION_MISMATCH' };
  }

  const { fetchPrivyCustomToken } = await import('@/api/privy-custom-token');
  const customJwt = await fetchPrivyCustomToken();
  try {
    const linked = await client.auth.customProvider.linkWithToken(customJwt);
    return { linked: true, user: linked?.user ?? currentUser };
  } catch (error) {
    const message = String(error?.message ?? error ?? '').toLowerCase();
    if (message.includes('already linked') || message.includes('already exists')) {
      return { linked: true, user: currentUser, alreadyLinked: true };
    }
    console.warn('[privy] custom-auth legacy wallet link failed', error);
    return { linked: false, code: 'CUSTOM_AUTH_LINK_FAILED', error };
  }
}

/**
 * 用平台签发的 Privy Custom JWT 登录（custom_auth），与 App 端同一 DID / 托管钱包。
 * 对应 js-sdk-core：auth.customProvider.syncWithToken
 *
 * @param {string} customJwt
 * @param {{ expectedAddress?: string, createIfMissing?: boolean }} [opts]
 *   expectedAddress：已登记资金地址时传入；缺钱包或地址不符则失败，**禁止** create 第二把。
 *   createIfMissing：默认 true（首次 FE 开钱包）；服务端预建后的 hydrate/soft-sync 须 false。
 */
export async function privyLoginWithCustomJwt(
  customJwt,
  { expectedAddress = '', createIfMissing = true } = {}
) {
  const token = String(customJwt ?? '').trim();
  if (!token) {
    throw new Error('Privy custom JWT missing');
  }
  const client = await getPrivyClient();
  if (!client) throw new Error('Privy is not configured');

  const expected = String(expectedAddress ?? '').trim();
  // 已有平台资金地址：只对齐会话，绝不 create（防服务端预建后再造第二钱包）
  const allowCreate = Boolean(createIfMissing) && !expected;
  if (expected) {
    try {
      const currentUser = await getPrivySessionUser();
      const currentWallet = getUserEmbeddedEthereumWallet(currentUser);
      if (currentWallet?.address?.toLowerCase() === expected.toLowerCase()) {
        // 历史账号可能只有当前会话仍能访问原托管钱包；先复用，不能先 logout。
        await ensurePrivyEmbeddedIframePing(client);
        return { user: currentUser, accessToken: await getPrivyAccessToken() };
      }
    } catch (error) {
      console.warn('[privy] current registered-wallet session probe failed', error);
    }
  }

  // 当前会话未命中后台资金身份时，再清理旧 oauth/email session 并走 custom_auth。
  await logoutPrivyIfSession();
  await ensurePrivyEmbeddedIframePing(client);

  const session = await client.auth.customProvider.syncWithToken(token);
  const result = await finalizePrivyLogin(session, { createIfMissing: allowCreate });
  const wallet = getUserEmbeddedEthereumWallet(result.user);
  const address = String(wallet?.address ?? '').trim();
  if (expected) {
    if (!address) {
      const err = new Error('Privy embedded wallet missing after sync');
      err.code = 'NO_EMBEDDED_WALLET';
      throw err;
    }
    if (address.toLowerCase() !== expected.toLowerCase()) {
      const err = new Error('Privy signer address mismatch');
      err.code = 'PRIVY_SIGNER_MISMATCH';
      throw err;
    }
  }
  return result;
}

/**
 * 平台 Session 已落盘后：拉 custom-token → Privy custom_auth 登录并确保嵌入式钱包。
 * @param {{ expectedAddress?: string, createIfMissing?: boolean }} [opts]
 */
export async function syncPrivyAfterPlatformLogin({
  expectedAddress = '',
  createIfMissing = true,
} = {}) {
  const { fetchPrivyCustomToken } = await import('@/api/privy-custom-token');
  const customJwt = await fetchPrivyCustomToken();
  return privyLoginWithCustomJwt(customJwt, { expectedAddress, createIfMissing });
}

/**
 * @deprecated 外部钱包登录已改回自管 challenge（addressType=1）；勿用于登录主路径。
 * 保留函数体供临时调试 / 回滚对照。
 */
export async function loginPrivyWithExternalWallet({
  address,
  provider,
  walletClientType = 'unknown',
  connectorType = 'injected',
  signal,
} = {}) {
  if (!provider?.request) {
    throw new Error('External wallet provider is unavailable');
  }

  const client = await ensurePrivyAuthReady();
  // 避免「已登录」冲突：清掉残留 Privy session 再 SIWE
  await logoutPrivyIfSession();

  const { getAddress } = await loadEthers();
  const checksumAddress = getAddress(String(address ?? '').trim());
  if (!checksumAddress) {
    throw new Error('External wallet address is missing');
  }

  let chainIdNum = getExpectedChainId();
  try {
    const hex = await provider.request({ method: 'eth_chainId' });
    const parsed = parsePrivyChainId(hex);
    if (Number.isFinite(parsed) && parsed > 0) chainIdNum = parsed;
  } catch {
    /* 用配置链兜底 */
  }

  if (signal?.aborted) {
    const err = new Error('Aborted');
    err.name = 'AbortError';
    throw err;
  }

  const externalWallet = {
    address: checksumAddress,
    chainId: `eip155:${chainIdNum}`,
    walletClientType,
    connectorType,
  };

  const domain = typeof window !== 'undefined' ? window.location.host : '';
  const uri = typeof window !== 'undefined' ? window.location.origin : '';
  const { message } = await client.auth.siwe.init(externalWallet, domain, uri);

  if (signal?.aborted) {
    const err = new Error('Aborted');
    err.name = 'AbortError';
    throw err;
  }

  const signature = await provider.request({
    method: 'personal_sign',
    params: [message, checksumAddress],
  });

  const session = await client.auth.siwe.loginWithSiwe(String(signature ?? '').trim());
  return finalizePrivyLogin(session);
}

/**
 * @deprecated 多端钱包一致性：PC 关闭 Privy 内置 google_oauth，改 Firebase idToken → 平台 /login/google → custom-token。
 * 勿用于登录主路径。
 */
export async function privyLoginWithGoogle() {
  const client = await getPrivyClient();
  if (!client) throw new Error('Privy is not configured');
  const redirectURI = `${getPrivyAppOrigin()}/auth/privy-callback`;
  const oauthURL = await client.auth.oauth.generateURL('google', redirectURI);
  const target = typeof oauthURL === 'string' ? oauthURL : oauthURL?.url;
  if (!target) {
    throw new Error('Failed to generate Privy OAuth URL');
  }
  window.location.assign(target);
}

/**
 * @deprecated 仅兼容旧 OAuth 回跳；新 Google 登录不再走 Privy OAuth。
 */
export async function privyHandleOAuthCallback() {
  const client = await getPrivyClient();
  if (!client) throw new Error('Privy is not configured');

  const queryParams = new URLSearchParams(window.location.search);
  const oauthCode = queryParams.get('privy_oauth_code');
  const oauthState = queryParams.get('privy_oauth_state');
  if (!oauthCode || !oauthState) {
    throw new Error('Missing Privy OAuth callback parameters');
  }

  const session = await client.auth.oauth.loginWithCode(oauthCode, oauthState);
  return finalizePrivyLogin(session);
}

export async function getPrivyAccessToken() {
  const client = await getPrivyClient();
  if (!client) return null;
  return client.getAccessToken();
}

export async function getPrivySessionUser() {
  const client = await getPrivyClient();
  if (!client) return null;
  const { user } = await client.user.get();
  return user ?? null;
}

/** 归一化 Privy linked accounts（SDK 为 linked_accounts，部分形态为 linkedAccounts） */
function getPrivyLinkedAccounts(user) {
  if (!user) return [];
  if (Array.isArray(user.linkedAccounts)) return user.linkedAccounts;
  if (Array.isArray(user.linked_accounts)) return user.linked_accounts;
  return [];
}

/** 从 Privy user 提取邮箱（邮箱登录 / Google OAuth 账号） */
export function extractPrivyEmail(user) {
  if (!user) return '';

  const fromGoogleShortcut = String(user.google?.email ?? '').trim();
  if (fromGoogleShortcut) return fromGoogleShortcut;

  if (user.email?.address) return String(user.email.address).trim();
  if (typeof user.email === 'string' && user.email.trim()) return user.email.trim();

  const linked = getPrivyLinkedAccounts(user);
  const emailAccount = linked.find((item) => item?.type === 'email');
  const fromEmail = String(emailAccount?.address ?? emailAccount?.email ?? '').trim();
  if (fromEmail) return fromEmail;

  const googleAccount = linked.find(
    (item) => item?.type === 'google_oauth' || item?.type === 'google'
  );
  return String(googleAccount?.email ?? googleAccount?.address ?? '').trim();
}

export function buildPrivyBindPayload(user, privyToken, inviteCode) {
  const wallet = getUserEmbeddedEthereumWallet(user);
  return {
    privyUserId: user?.id ?? '',
    walletAddress: wallet?.address ?? '',
    email: extractPrivyEmail(user) || undefined,
    privyToken,
    inviteCode: inviteCode || undefined,
  };
}

/** 从 Privy user 提取交易地址登记所需字段 */
export function extractPrivyTradeAddressFields(user) {
  const wallet = getUserEmbeddedEthereumWallet(user);
  const address = String(wallet?.address ?? '').trim();
  const privyWalletId = String(wallet?.id ?? wallet?.walletId ?? wallet?.wallet_id ?? '').trim();
  return { address, privyWalletId };
}

export async function getPrivyEmbeddedProvider() {
  const client = await getPrivyClient();
  if (!client) return null;

  const { user } = await client.user.get();
  if (!user) return null;

  const wallet = getUserEmbeddedEthereumWallet(user);
  if (!wallet) return null;

  const { entropyId, entropyIdVerifier } = getEntropyDetailsFromUser(user);
  return client.embeddedWallet.getEthereumProvider({
    wallet,
    entropyId,
    entropyIdVerifier,
  });
}

export function isPrivyEmbeddedLogin() {
  return getWalletProviderId() === 'privy';
}

/**
 * 托管交易登录：邮箱 / Google（及 provider=privy）。
 * 此类用户不弹「启用交易」签名窗；WALLET 换绑 type=2 见 useTradeDusdApprovalPrefetch（hasCustodial）。
 */
export function isPrivyCustodialLogin() {
  if (isPrivyEmbeddedLogin()) return true;
  const origin = String(getOriginLoginType() ?? '')
    .trim()
    .toUpperCase();
  return origin === 'EMAIL' || origin === 'GOOGLE';
}

/**
 * 是否展示充值入口：邮箱/Google 托管会话，或已落盘钱包地址（含 MetaMask 等自管）。
 * 与 isPrivyCustodialLogin / sessionExpectsCustodialWallet 对齐；勿只认 provider=privy
 *（平台 Session 先登录、老号 type=2 跳过 Privy 时 provider 可能尚未回填）。
 */
export function canShowDeposit() {
  if (!hasAuthSession()) return false;
  return isPrivyCustodialLogin() || Boolean(String(getWalletAddress() || '').trim());
}

/** 是否展示提现入口（与充值同口径） */
export function canShowWithdraw() {
  return canShowDeposit();
}

/** @deprecated 使用 canShowWithdraw；保留别名避免旧引用断裂 */
export function canShowPrivyWithdraw() {
  return canShowWithdraw();
}

/** @deprecated 使用 canShowDeposit；保留别名避免旧引用断裂 */
export function canShowPrivyDeposit() {
  return canShowDeposit();
}

/** 是否 Privy 嵌入式钱包签名会话过期类错误 */
export function isPrivySigningSessionError(err) {
  const code = String(err?.code ?? err?.privyErrorCode ?? '').trim();
  const raw = [
    err?.message,
    err?.error,
    err?.shortMessage,
    err?.cause?.message,
    err?.info?.error?.message,
    err?.data?.message,
  ]
    .filter(Boolean)
    .map((s) => String(s).toLowerCase())
    .join(' ');
  if (
    code === 'embedded_wallet_request_error' ||
    code === 'must_be_authenticated' ||
    code === 'embedded_wallet_before_logged_in' ||
    code === 'NO_PRIVY_TOKEN' ||
    code === 'NO_PRIVY_USER' ||
    code === 'NO_EMBEDDED_WALLET'
  ) {
    return true;
  }
  return (
    raw.includes('missing privy token') ||
    raw.includes('no tokens found in storage') ||
    raw.includes('failed to download media') ||
    raw.includes('must be logged in') ||
    (raw.includes('session') && raw.includes('expir')) ||
    (raw.includes('embedded wallet') && raw.includes('not ready'))
  );
}

/**
 * 轻量探测：托管轨下 Privy 会话 + 嵌入式钱包是否可签名（不强制 sync）。
 * @returns {Promise<{ ok: boolean, code?: string, skipped?: boolean }>}
 */
export async function probePrivySignSession() {
  const { getWalletAddress, hasAuthSession } = await import('@/utils/auth-token');
  const { sessionExpectsCustodialWallet } = await import('@/utils/custodial-session-gate');
  if (!hasAuthSession()) return { ok: false, code: 'NO_SESSION' };
  if (!sessionExpectsCustodialWallet()) return { ok: true, skipped: true };
  if (!isPrivyConfigured()) return { ok: false, code: 'NOT_CONFIGURED' };

  try {
    await ensurePrivyAuthReady();
    const token = await getPrivyAccessToken();
    if (!token) return { ok: false, code: 'NO_PRIVY_TOKEN' };

    const user = await getPrivySessionUser();
    if (!user?.id) return { ok: false, code: 'NO_PRIVY_USER' };

    const wallet = getUserEmbeddedEthereumWallet(user);
    if (!wallet?.address) return { ok: false, code: 'NO_EMBEDDED_WALLET' };

    const expectedAddress = String(getWalletAddress() ?? '').trim();
    if (!expectedAddress) return { ok: false, code: 'NO_EXPECTED_WALLET' };
    if (wallet.address.toLowerCase() !== expectedAddress.toLowerCase()) {
      return { ok: false, code: 'PRIVY_SIGNER_MISMATCH' };
    }

    await ensurePrivyEmbeddedIframePing();
    const signerAddress = await getPrivyEmbeddedSignerAddress();
    if (!signerAddress || signerAddress.toLowerCase() !== expectedAddress.toLowerCase()) {
      return { ok: false, code: 'PRIVY_SIGNER_MISMATCH' };
    }
    return { ok: true };
  } catch (err) {
    if (isPrivySigningSessionError(err)) {
      return { ok: false, code: 'SESSION_ERROR' };
    }
    console.warn('[privy] probe sign session failed', err);
    return { ok: false, code: 'UNKNOWN' };
  }
}

/**
 * 静默重建 Privy 签名会话：custom JWT → syncWithToken（不因已有 type=2 跳过）。
 * @returns {Promise<{ ok: boolean, code?: string }>}
 */
export async function recoverPrivySignSession() {
  const { getWalletAddress, hasAuthSession, setWalletAddress, setWalletProviderId } = await import(
    '@/utils/auth-token'
  );
  const { sessionExpectsCustodialWallet } = await import('@/utils/custodial-session-gate');
  const { syncCustodialSessionFromTradeAddresses } = await import('@/utils/privy-platform-login');

  if (!hasAuthSession()) return { ok: false, code: 'NO_SESSION' };
  if (!sessionExpectsCustodialWallet()) return { ok: true };
  if (!isPrivyConfigured()) return { ok: false, code: 'NOT_CONFIGURED' };

  try {
    await syncCustodialSessionFromTradeAddresses();
    const expectedAddress = String(getWalletAddress() ?? '').trim();
    await ensurePrivyAuthReady();
    const { user } = await syncPrivyAfterPlatformLogin({
      expectedAddress,
      createIfMissing: false,
    });
    const wallet = getUserEmbeddedEthereumWallet(user);
    const address = String(wallet?.address ?? '').trim();
    if (!address) return { ok: false, code: 'NO_EMBEDDED_WALLET' };
    if (expectedAddress && address.toLowerCase() !== expectedAddress.toLowerCase()) {
      return { ok: false, code: 'PRIVY_SIGNER_MISMATCH' };
    }
    setWalletProviderId('privy');
    if (!expectedAddress) setWalletAddress(address);
    const probe = await probePrivySignSession();
    if (probe.ok) return { ok: true };
    return { ok: false, code: probe.code || 'RECOVER_FAILED' };
  } catch (err) {
    console.warn('[privy] recover sign session failed', err);
    if (isPrivySigningSessionError(err)) {
      return { ok: false, code: 'SESSION_ERROR' };
    }
    return { ok: false, code: 'RECOVER_FAILED' };
  }
}

/**
 * Privy 下注/签名前：init → ping iframe → 确认 user + 嵌入式钱包存在
 */
export async function ensurePrivyEmbeddedReady() {
  if (!isPrivyEmbeddedLogin()) return null;

  const client = await getPrivyClient();
  if (!client) {
    throw new Error('Privy embedded wallet is not ready');
  }

  await ensurePrivyEmbeddedIframePing(client);

  const { user } = await client.user.get();
  if (!user?.id) {
    throw new Error('Privy embedded wallet is not ready');
  }

  const wallet = getUserEmbeddedEthereumWallet(user);
  if (!wallet?.address) {
    throw new Error('Privy embedded wallet is not ready');
  }

  return { client, user, wallet };
}

/**
 * Privy 嵌入式钱包切链（wallet_switchEthereumChain，不用 wallet_addEthereumChain）
 * @param {number} [chainId]
 */
export async function switchPrivyEmbeddedChain(chainId = getExpectedChainId()) {
  const provider = await getPrivyEmbeddedProvider();
  if (!provider?.request) {
    throw new Error('Privy embedded wallet provider is unavailable');
  }

  const expected = Number(chainId);
  if (!Number.isFinite(expected) || expected <= 0) {
    throw new Error('invalid chain id');
  }

  const currentHex = await provider.request({ method: 'eth_chainId' });
  const current = parsePrivyChainId(currentHex);
  if (current === expected) {
    return provider;
  }

  const targetHex = `0x${expected.toString(16)}`;
  try {
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: targetHex }],
    });
  } catch (error) {
    console.warn('[privy] wallet_switchEthereumChain failed', error);
    throw error;
  }

  const afterHex = await provider.request({ method: 'eth_chainId' });
  const after = parsePrivyChainId(afterHex);
  if (after !== expected) {
    throw new Error('wrong network');
  }

  return provider;
}

/** Privy 嵌入式钱包切到配置链（授权/签名/发交易前调用） */
export async function ensurePrivyEmbeddedChain() {
  if (getWalletProviderId() !== 'privy') return null;
  return switchPrivyEmbeddedChain(getExpectedChainId());
}

/** 获取 Privy 嵌入式钱包当前 signer 地址 */
export async function getPrivyEmbeddedSignerAddress() {
  const provider = await ensurePrivyEmbeddedChain();
  if (!provider) return '';

  const { BrowserProvider } = await loadEthers();
  const browserProvider = new BrowserProvider(provider);
  const signer = await browserProvider.getSigner();
  return signer.getAddress();
}

export async function logoutPrivy() {
  const client = await getPrivyClient();
  if (!client) return;

  try {
    const { user } = await client.user.get();
    if (user?.id) {
      await client.auth.logout({ userId: user.id });
    }
  } catch (error) {
    if (error?.name !== 'AbortError') {
      console.warn('[privy] logout failed', error);
    }
  }
}

export function consumePrivyInviteCode() {
  if (typeof sessionStorage === 'undefined') return '';
  const code = String(sessionStorage.getItem('privy-invite-code') ?? '').trim();
  sessionStorage.removeItem('privy-invite-code');
  return code;
}

export function storePrivyInviteCode(code) {
  if (typeof sessionStorage === 'undefined') return;
  const value = String(code ?? '').trim();
  if (value) sessionStorage.setItem('privy-invite-code', value);
  else sessionStorage.removeItem('privy-invite-code');
}
